import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { usePopup } from '../../../../assets/contexts/PopupContext';
import PocProgramSupportPreview from '../report/forms/PocProgramSupportPreview';
import pocProgramSupportStructure from '../report/data/poc_program_support.structure.json';
import '../report/styles/documentFormBuilder.css';

/** structure + flat 데이터로 layoutRows 생성 (PoC 프로그램 수행 지원서 형식) */
const buildLayoutRowsFromFlat = (structure, flatData) => {
  const layout = structure?.layout || [];
  const layoutRows = {};
  layout.forEach((block, idx) => {
    const items = block?.type === 'DIVIDER' ? (block.items || []) : (block.fields || []);
    const keys = items.map((it) => it.key).filter(Boolean);
    const row = {};
    keys.forEach((k) => {
      row[k] = flatData?.[k] ?? '';
    });
    layoutRows[idx] = [row];
  });
  return layoutRows;
};

/** 지원자 데이터를 PoC 프로그램 수행 지원서 layoutRows로 변환 (기존 참가 신청 필드 매핑 포함) */
const applicantToPocSupportData = (applicant) => {
  if (!applicant) return { layoutRows: {} };
  if (applicant.dataJson?.layoutRows) {
    return { layoutRows: applicant.dataJson.layoutRows };
  }
  const f = applicant.formValues || {};
  const flat = {
    task_name: f.apply_title ?? '',
    team_name: '',
    partner_org: f.org_name ?? '',
    prof_name: f.contact_person ?? '',
    prof_phone: f.phone ?? '',
    student_leader: '',
    student_phone: '',
    participant_type: '',
    participant_count: '',
    participant_names: '',
    student_name: '',
    student_role_desc: '',
    background_text: f.experience ?? '',
    solution_text: '',
    goal_text: '',
    method_text: '',
    data_text: '',
    expected_text: '',
    academic_text: '',
    industry_text: '',
    spend_date: '',
    spend_item: '',
    spend_detail: '',
    spend_amount: '',
    spend_note: '',
    submit_date: f.submit_date ?? '',
  };
  return {
    layoutRows: buildLayoutRowsFromFlat(pocProgramSupportStructure, flat),
  };
};

/** 목업: 공고별 지원자 목록 (실제 연동 시 API로 대체) */
const MOCK_APPLICANTS_BY_DEMAND_ID = {
  1: [
    {
      id: 101,
      org_name: '㈜메디코슨',
      contact_person: '김상범',
      apply_title: 'AI 품질 검사 솔루션 참가 신청',
      submittedAt: '2025-01-15T10:30:00',
      formValues: {
        apply_title: 'AI 품질 검사 솔루션 참가 신청',
        org_name: '㈜메디코슨',
        contact_person: '김상범',
        phone: '010-2965-1690',
        email: 'support@medicoson.com',
        experience: '비전 AI 기반 품질 검사 시스템 3건 구축 경험',
        attachment: '',
        privacy_agreement: true,
        submit_date: '2025-01-15'
      },
      signatureImage: null
    },
    {
      id: 102,
      org_name: '한국산업기술연구원',
      contact_person: '이연구',
      apply_title: '딥러닝 기반 불량 검출 연구 참가',
      submittedAt: '2025-01-14T14:20:00',
      formValues: {
        apply_title: '딥러닝 기반 불량 검출 연구 참가',
        org_name: '한국산업기술연구원',
        contact_person: '이연구',
        phone: '02-1234-5678',
        email: 'research@kit.re.kr',
        experience: '제조업 품질 검사 AI 프로젝트 다수 수행',
        attachment: '',
        privacy_agreement: true,
        submit_date: '2025-01-14'
      },
      signatureImage: null
    }
  ],
  2: [], 3: [], 4: [], 5: []
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * 지원자 관리 팝업
 * config: { demandId, demandTitle }
 * 우측: 문서폼양식 PoC 프로그램 수행 지원서 화면으로 미리보기
 */
const TechDemandApplicantsPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const { openTextPopup } = usePopup();
  const demandId = config?.demandId;
  const demandTitle = config?.demandTitle || t('popup.techDemandApplicantsPopup.noticeFallback', { demandId });

  const applicants = useMemo(
    () => (demandId ? (MOCK_APPLICANTS_BY_DEMAND_ID[demandId] || []) : []),
    [demandId]
  );

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewApplicant, setPreviewApplicant] = useState(null);

  const handleViewForm = useCallback((applicant) => {
    setPreviewApplicant(applicant);
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === applicants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(applicants.map((a) => a.id)));
    }
  }, [applicants, selectedIds.size]);

  const handleCreatePrePocRoom = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const result = await openTextPopup({
      title: t('popup.techDemandApplicantsPopup.prePocRoomTitle'),
      content: `<p>${t('popup.techDemandApplicantsPopup.createPrePocConfirmHtml', { count: selectedIds.size })}</p><p class="mt-2 small text-muted">${t('popup.techDemandApplicantsPopup.createPrePocDesc')}</p>`,
      leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
      rightButton: { text: t('popup.techDemandApplicantsPopup.create'), color: 'btn-primary', submit: true },
      displayType: 'modal'
    });
    if (result?.submit) {
      setSelectedIds(new Set());
      await openTextPopup({
        title: t('popup.techDemandApplicantsPopup.receivedTitle'),
        content: `<p>${t('popup.techDemandApplicantsPopup.receivedMessage')}</p><p class="mt-2 small text-muted">${t('popup.techDemandApplicantsPopup.receivedDesc')}</p>`,
        leftButton: null,
        rightButton: { text: t('popup.common.confirm'), color: 'btn-primary', submit: true },
        displayType: 'modal'
      });
    }
  }, [selectedIds, openTextPopup, t]);

  if (!demandId) {
    return (
      <PopupWrapper config={{ ...config, title: config?.title || t('popup.techDemandApplicantsPopup.title') }} onClose={onClose} displayType="modal">
        <div className="main-popup-body p-4">
          <p className="mb-0 text-muted">{t('popup.techDemandApplicantsPopup.noNoticeInfo')}</p>
        </div>
        <div className="main-popup-footer d-flex justify-content-end">
          <button type="button" className="btn btn-secondary main-popup-cancel-accent" onClick={() => onClose(false)}>{t('popup.common.close')}</button>
        </div>
      </PopupWrapper>
    );
  }

  return (
    <PopupWrapper
      config={{ ...config, title: config?.title || t('popup.techDemandApplicantsPopup.title') }}
      onClose={onClose}
      displayType="modal"
      className="popup-tech-demand-applicants"
    >
      <div className="main-popup-body popup-tech-demand-applicants-body">
        <p className="popup-tech-demand-applicants-desc mb-3">
          {t('popup.techDemandApplicantsPopup.description', { demandTitle })}
        </p>

        <div className="popup-tech-demand-applicants-dual">
          {/* 상단: 지원자 목록 (작은 카드형) */}
          <div className="popup-tech-demand-applicants-list">
            <div className="popup-tech-demand-applicants-list-head">
              <h4 className="popup-tech-demand-applicants-list-title">{t('popup.techDemandApplicantsPopup.applicantList')}</h4>
              {applicants.length > 0 && (
                <label className="popup-tech-demand-applicants-check-label">
                  <input
                    type="checkbox"
                    checked={applicants.length > 0 && selectedIds.size === applicants.length}
                    onChange={toggleSelectAll}
                    aria-label={t('popup.techDemandApplicantsPopup.selectAllAria')}
                  />
                  <span>{t('popup.techDemandApplicantsPopup.selectAll')}</span>
                </label>
              )}
            </div>
            {applicants.length === 0 ? (
              <div className="popup-tech-demand-applicants-empty">
                <p className="mb-0">{t('popup.techDemandApplicantsPopup.empty')}</p>
              </div>
            ) : (
              <div className="popup-tech-demand-applicants-cards">
                {applicants.map((app, index) => (
                  <div
                    key={app.id}
                    role="button"
                    tabIndex={0}
                    className={`popup-tech-demand-applicants-card ${previewApplicant?.id === app.id ? 'popup-tech-demand-applicants-card-active' : ''}`}
                    onClick={() => handleViewForm(app)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewForm(app);
                      }
                    }}
                  >
                    <div className="popup-tech-demand-applicants-card-check" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(app.id)}
                        onChange={() => toggleSelect(app.id)}
                        aria-label={t('popup.techDemandApplicantsPopup.selectApplicantAria', { name: app.org_name || t('popup.techDemandApplicantsPopup.applicantFallback') })}
                      />
                    </div>
                    <div className="popup-tech-demand-applicants-card-body">
                      <div className="popup-tech-demand-applicants-card-title">{app.org_name || '-'}</div>
                      <div className="popup-tech-demand-applicants-card-meta">
                        {app.contact_person || '-'} · {app.apply_title ? (app.apply_title.length > 20 ? `${app.apply_title.slice(0, 20)}…` : app.apply_title) : '-'}
                      </div>
                      <div className="popup-tech-demand-applicants-card-date">{formatDateTime(app.submittedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 하단: PoC 프로그램 수행 지원서 미리보기 */}
          <div className="popup-tech-demand-applicants-preview">
            <h4 className="popup-tech-demand-applicants-preview-title">{t('popup.techDemandApplicantsPopup.previewTitle')}</h4>
            {previewApplicant ? (
              <div className="popup-tech-demand-applicants-preview-scroll">
                <div className="popup-tech-demand-applicants-poc-preview-wrap">
                  <PocProgramSupportPreview
                    signatureDataUrl={previewApplicant.signatureImage || undefined}
                    structureJson={pocProgramSupportStructure}
                    dataJson={applicantToPocSupportData(previewApplicant)}
                  />
                </div>
              </div>
            ) : (
              <div className="popup-tech-demand-applicants-preview-placeholder">
                <p className="mb-0">{t('popup.techDemandApplicantsPopup.previewPlaceholder')}</p>
              </div>
            )}
          </div>
        </div>

        <div className="popup-tech-demand-applicants-actions mt-4">
          {applicants.length > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={selectedIds.size === 0}
              onClick={handleCreatePrePocRoom}
            >
              {t('popup.techDemandApplicantsPopup.createWithSelection')} {selectedIds.size > 0 && t('popup.techDemandApplicantsPopup.selectedCount', { count: selectedIds.size })}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .popup-tech-demand-applicants-body {
          max-width: 100%;
          min-width: 0;
          display: flex;
          flex-direction: column;
          min-height: 0;
          max-height: 85vh;
        }
        .popup-tech-demand-applicants-desc {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
          flex-shrink: 0;
        }
        .popup-tech-demand-applicants-dual {
          display: flex;
          flex-direction: column;
          gap: 20px;
          min-height: 0;
          flex: 1;
          overflow-y: auto;
        }
        .popup-tech-demand-applicants-list {
          flex-shrink: 0;
          min-width: 0;
        }
        .popup-tech-demand-applicants-list-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }
        .popup-tech-demand-applicants-list-title {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }
        .popup-tech-demand-applicants-check-label {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
          cursor: pointer;
          margin: 0;
        }
        .popup-tech-demand-applicants-check-label input { width: 14px; height: 14px; cursor: pointer; }
        .popup-tech-demand-applicants-cards {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .popup-tech-demand-applicants-card {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #fff;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
          text-align: left;
          min-width: 0;
          max-width: 280px;
        }
        .popup-tech-demand-applicants-card:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .popup-tech-demand-applicants-card-active {
          background: #f0f9ff;
          border-color: #0ea5e9;
        }
        .popup-tech-demand-applicants-card-check {
          flex-shrink: 0;
          padding-top: 2px;
        }
        .popup-tech-demand-applicants-card-check input { width: 14px; height: 14px; cursor: pointer; }
        .popup-tech-demand-applicants-card-body { flex: 1; min-width: 0; }
        .popup-tech-demand-applicants-card-title {
          font-weight: 600;
          font-size: 13px;
          color: #111827;
          margin-bottom: 2px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .popup-tech-demand-applicants-card-meta {
          font-size: 11px;
          color: #6b7280;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 2px;
        }
        .popup-tech-demand-applicants-card-date {
          font-size: 11px;
          color: #9ca3af;
        }
        .popup-tech-demand-applicants-empty { padding: 24px 16px; text-align: center; color: #6b7280; }
        .popup-tech-demand-applicants-preview {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;
          background: #fafafa;
          min-height: 200px;
          max-height: 320px;
          display: flex;
          flex-direction: column;
          min-width: 0;
          flex-shrink: 0;
        }
        .popup-tech-demand-applicants-preview-title {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px 0;
          flex-shrink: 0;
        }
        .popup-tech-demand-applicants-preview-scroll {
          flex: 1;
          min-height: 0;
          overflow: auto;
        }
        .popup-tech-demand-applicants-preview-placeholder {
          padding: 48px 24px;
          text-align: center;
          color: #9ca3af;
          font-size: 14px;
        }
        .popup-tech-demand-applicants-poc-preview-wrap {
          font-size: 12px;
          line-height: 1.4;
          min-width: min-content;
        }
        .popup-tech-demand-applicants-poc-preview-wrap .support-preview-shell { padding: 0; }
        .popup-tech-demand-applicants-poc-preview-wrap .poc-table { font-size: inherit; }
        .popup-tech-demand-applicants-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          justify-content: flex-end;
          flex-shrink: 0;
        }
        .popup-tech-demand-applicants-actions .btn:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </PopupWrapper>
  );
};

export default TechDemandApplicantsPopup;
